import { useCallback, useEffect, useRef, useState } from 'react';
import { Flex, useColorMode } from '@chakra-ui/react';

import { Loader } from '../../ui';
import { useCalendarSettings, useModal, styles } from '../../../lib';
import { EventImage } from './EventImage';
import { EventDates } from './EventDates';
import { EventDetails } from './EventDetails';

import type { IEvent } from '../../../lib';

interface IEventDetailCardProps {
	event: IEvent;
	isSameDayEvent: boolean;
	isListItem?: boolean;
}

export const EventDetailCard = ({ event, isSameDayEvent, isListItem }: IEventDetailCardProps) => {
	const { colorMode } = useColorMode();
	const { openModal } = useModal();
	const { settings } = useCalendarSettings();
	const parentRef = useRef<HTMLDivElement>(null);
	const [imageHeight, setImageHeight] = useState<string>('auto');

	const handleOpenModal = useCallback(() => {
		openModal({ event, isSameDayEvent });
	}, [event, isSameDayEvent, openModal]);

	useEffect(() => {
		if (parentRef.current) {
			const parentHeight = parentRef.current.clientHeight;
			setImageHeight(`${parentHeight}px`);
		}
	}, [event.image]);

	if (!settings?.formats) return <Loader />;

	const eventPopoverColor = isListItem
		? event.color ||
			settings.eventCategoryColors[event.eventType?.[0]]?.background ||
			settings.styles[colorMode]?.eventColor
		: settings.styles[colorMode]?.eventTooltipColor || 'hsl(252, 13%, 46%)';

	const eventTooltipTextColor = isListItem
		? event.textColor || settings.styles[colorMode]?.eventTextColor
		: settings.styles[colorMode]?.eventTooltipTextColor || 'white';

	const haveFeaturedImage = isListItem && settings.enableFeaturedImage && Boolean(event.image);

	const { monthFormat, timeFormat } = settings.formats;
	const { minWidthProps } = styles.eventDetailCard;
	return (
		<Flex
			flexDirection={isListItem ? 'row' : 'column'}
			alignItems={isListItem && haveFeaturedImage ? 'center' : 'flex-start'}
			my={isListItem ? 4 : 0}
			bg={!haveFeaturedImage ? eventPopoverColor : ''}
			borderRadius='lg'
			boxShadow={isListItem ? 'none' : 'dark-lg'}
			className='event-popover'
			color='white'
			cursor='pointer'
			minW={minWidthProps}
			margin={0}
			onClick={handleOpenModal}
		>
			<EventImage
				event={event}
				isListItem={isListItem}
				imageHeight={imageHeight}
				haveFeaturedImage={haveFeaturedImage}
			/>
			{!haveFeaturedImage && (
				<EventDates
					event={event}
					isSameDayEvent={isSameDayEvent}
					eventPopoverColor={eventPopoverColor}
					eventTooltipTextColor={eventTooltipTextColor}
					isListItem={isListItem}
				/>
			)}
			<EventDetails
				event={event}
				isSameDayEvent={isSameDayEvent}
				eventTooltipTextColor={eventTooltipTextColor}
				monthFormat={monthFormat}
				timeFormat={timeFormat}
				isListItem={isListItem}
				eventPopoverColor={eventPopoverColor}
				haveFeaturedImage={haveFeaturedImage}
				parentRef={parentRef}
			/>
		</Flex>
	);
};
